import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import EMPAccordion from "../../shared/components/EMPAccordion/EMPAccordion";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import CheckBox from "../../shared/checkBox/CheckBox";
import ChecklistPDF from "../../onboarding/ChecklistPDF";
import MotionCautionIcon from "../../shared/Icons/MotionCautionIcon";
import {
  HAS_EMP_ONBOARD_ROLE,
  IS_CANADA_MARKET,
  IS_FORD_BRAND,
} from "../../utils/EmpUtil";
import PreOrderUEVClient from "../../clients/PreOrderUEVClient";
import useDealerAgreementStatus, {
  dealerAgreementStatusKey,
} from "../../shared/hooks/useDealerAgreementStatus";
import { formatTermsAcceptedDate } from "../../shared/utils/onboardingProgressUtil";
import { getTermsAcceptanceSummary } from "../../utils/enrollmentTermsUtil";
import CustomerHandlingEntitlement from "./CustomerHandlingEntitlement";
import styles from "./CanadianPreOrderEnrollmentPage.module.scss";

const PLACEHOLDER_TERMS_URL = `${process.env.PUBLIC_URL}/pre-order-terms-placeholder.pdf`;

const EnrollmentSteps = ({ user, onAgreementStatusChange }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dealerCommonId = user?.commonId || user?.commonDealerId;
  const isEligible =
    IS_CANADA_MARKET(user?.market) && HAS_EMP_ONBOARD_ROLE(user);
  const agreementStatus = useDealerAgreementStatus(dealerCommonId, {
    enabled: isEligible,
  });
  const saveAgreement = useMutation({
    mutationFn: (payload) =>
      PreOrderUEVClient.createOrUpdateStepStatus(payload),
    onSuccess: async (_, payload) => {
      const queryKey = dealerAgreementStatusKey(payload.dealerCommonId);
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(queryKey, {
        dealerCommonId: payload.dealerCommonId,
        dealerName: payload.dealerName,
        status: payload.acceptance,
        updatedBy: payload.updatedBy,
      });
      await queryClient.invalidateQueries({ queryKey });
    },
  });
  const termsAccepted = agreementStatus.data?.status === "ACCEPTED";
  const acceptanceSummaryText = termsAccepted
    ? getTermsAcceptanceSummary(t, {
        date: formatTermsAcceptedDate(agreementStatus.data?.updatedAt),
        cdsid: agreementStatus.data?.updatedBy,
      })
    : null;
  // A 404 explicitly means that this dealer has not recorded an agreement yet.
  const hasNoAgreement = agreementStatus.error?.response?.status === 404;
  const statusLoadError = agreementStatus.isError;
  useEffect(() => {
    if (
      !agreementStatus.isFetching &&
      (agreementStatus.isSuccess || agreementStatus.isError)
    ) {
      onAgreementStatusChange?.(
        dealerCommonId,
        agreementStatus.isSuccess ? agreementStatus.data?.status : undefined
      );
    }
  }, [
    dealerCommonId,
    agreementStatus.data?.status,
    agreementStatus.isSuccess,
    agreementStatus.isError,
    agreementStatus.isFetching,
    onAgreementStatusChange,
  ]);
  const canAccept =
    isEligible &&
    !!dealerCommonId &&
    !!user?.dealerName &&
    !!user?.userId &&
    (agreementStatus.isSuccess || hasNoAgreement) &&
    !agreementStatus.isFetching;
  const [expanded, setExpanded] = useState({
    terms: true,
    customerHandling: false,
  });
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [termsCheckboxChecked, setTermsCheckboxChecked] = useState(false);
  const [customerHandlingComplete, setCustomerHandlingComplete] =
    useState(false);
  const isFordBrand = IS_FORD_BRAND(user?.brand);

  const toggleAccordion = (name) => () =>
    setExpanded((current) => ({ ...current, [name]: !current[name] }));

  const handleTermsScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isScrollable = scrollHeight > clientHeight;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 10;

    if (isScrollable && isAtBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleTermsAccept = () =>
    saveAgreement.mutate({
      dealerCommonId,
      dealerName: user.dealerName,
      acceptance: "ACCEPTED",
      updatedBy: user.userId,
    });

  return (
    <Box
      component="main"
      className={styles.page}
      data-testid="canadian-preorder-enrollment"
    >
      <Typography component="h1" className={styles.pageTitle}>
        {t("CanadianPreOrderEnrollment.title", {
          defaultValue: "Ford.com Pre-Order Enrollment",
        })}
      </Typography>
      <Box className={styles.accordions}>
        <EMPAccordion
          heading={t("CanadianPreOrderEnrollment.terms.heading", {
            defaultValue: "Terms & Conditions",
          })}
          expanded={expanded.terms}
          onChange={toggleAccordion("terms")}
          prefixIconType={termsAccepted ? "check" : "caution"}
          toggleIconColor="grey"
        >
          <Box className={styles.termsContainer}>
            <Box className={styles.introduction}>
              <Typography variant="body1" className={styles.introductionText}>
                {t("CanadianPreOrderEnrollment.terms.description", {
                  defaultValue:
                    "To participate in the Ford Fathom Pre-Order Program, you must accept the Participation Terms and Conditions below. As the terms and conditions are updated, your dealership must agree to them to remain in the experience.",
                })}
              </Typography>
            </Box>
            <Box className={styles.mainDealerNotice} role="note">
              <MotionCautionIcon className={styles.cautionIcon} />
              <Typography component="p">
                {t("CanadianPreOrderEnrollment.terms.mainDealerNotice", {
                  defaultValue:
                    "By accepting the Ford Fathom Pre-Order Program terms and conditions, as the main dealer you are also accepting them on behalf of all your satellite locations.",
                })}
              </Typography>
            </Box>
            <Box
              className={styles.termsBox}
              data-testid="preorder-terms-document"
              onScroll={handleTermsScroll}
            >
              <ChecklistPDF file={PLACEHOLDER_TERMS_URL} />
            </Box>
            <Box className={styles.downloadButtonContainer}>
              <EMPButton
                variant="text"
                href={PLACEHOLDER_TERMS_URL}
                download="pre-order-terms-placeholder.pdf"
                isFord={isFordBrand}
                leftIcon={<FileDownloadOutlinedIcon fontSize="small" />}
                sx={{ minWidth: "auto", padding: 0 }}
              >
                {t("CanadianPreOrderEnrollment.terms.download", {
                  defaultValue: "Download",
                })}
              </EMPButton>
              {termsAccepted && (
                <Typography
                  data-testid="terms-accepted"
                  component="p"
                  className={styles.acceptanceInfo}
                >
                  {acceptanceSummaryText ||
                    t("CanadianPreOrderEnrollment.terms.accepted", {
                      defaultValue: "Terms & Conditions accepted.",
                    })}
                </Typography>
              )}
            </Box>
            {agreementStatus.isFetching && (
              <Typography role="status">
                {t("CanadianPreOrderEnrollment.terms.loading", {
                  defaultValue: "Loading terms acceptance status...",
                })}
              </Typography>
            )}
            {statusLoadError && (
              <Box>
                <Typography role="alert">
                  {t("CanadianPreOrderEnrollment.terms.loadError", {
                    defaultValue:
                      "Could not load acceptance status. Please try again.",
                  })}
                </Typography>
                <EMPButton
                  variant="outlined"
                  isFord={isFordBrand}
                  disabled={agreementStatus.isFetching}
                  onClick={() => agreementStatus.refetch()}
                >
                  {t("CanadianPreOrderEnrollment.terms.retry", {
                    defaultValue: "Retry",
                  })}
                </EMPButton>
              </Box>
            )}
            {saveAgreement.isError && (
              <Typography role="alert">
                {t("CanadianPreOrderEnrollment.terms.acceptError", {
                  defaultValue: "Could not save acceptance. Please try again.",
                })}
              </Typography>
            )}
            {!termsAccepted && (
              <>
                <Box className={styles.checkboxContainer}>
                  <CheckBox
                    id="preorder-terms-checkbox"
                    checked={termsCheckboxChecked}
                    isDisabled={!hasScrolledToBottom}
                    handleOnChange={setTermsCheckboxChecked}
                    isFordBrand={isFordBrand}
                    ariaLabel={t(
                      "CanadianPreOrderEnrollment.terms.checkboxAriaLabel",
                      {
                        defaultValue:
                          "Accept Canadian pre-order terms and conditions",
                      }
                    )}
                    label={t("CanadianPreOrderEnrollment.terms.checkboxLabel", {
                      defaultValue:
                        "By checking the box, I acknowledge that the information provided is correct and that I am authorized (with F(ii) Authority) to complete the enrollment for my dealership.",
                    })}
                  />
                </Box>
                <Box className={styles.acceptButtonContainer}>
                  <EMPButton
                    variant="contained"
                    disabled={
                      !termsCheckboxChecked ||
                      saveAgreement.isLoading ||
                      !canAccept
                    }
                    onClick={handleTermsAccept}
                    isFord={isFordBrand}
                  >
                    {saveAgreement.isLoading
                      ? t("CanadianPreOrderEnrollment.terms.saving", {
                          defaultValue: "Saving...",
                        })
                      : t("CanadianPreOrderEnrollment.terms.acceptButton", {
                          defaultValue: "Accept Terms and Conditions",
                        })}
                  </EMPButton>
                </Box>
              </>
            )}
          </Box>
        </EMPAccordion>
        <EMPAccordion
          heading={t("CanadianPreOrderEnrollment.entitlements.heading", {
            defaultValue: "Entitlements for Customer Handling",
          })}
          expanded={expanded.customerHandling}
          onChange={toggleAccordion("customerHandling")}
          prefixIconType={customerHandlingComplete ? "check" : "caution"}
          toggleIconColor="grey"
        >
          <CustomerHandlingEntitlement
            user={user}
            onStatusChange={setCustomerHandlingComplete}
            isExpanded={expanded.customerHandling}
          />
        </EMPAccordion>
      </Box>
    </Box>
  );
};

EnrollmentSteps.propTypes = {
  onAgreementStatusChange: PropTypes.func,
  user: PropTypes.shape({
    brand: PropTypes.string,
    commonDealerId: PropTypes.string,
    commonId: PropTypes.string,
    dealerName: PropTypes.string,
    userId: PropTypes.string,
    market: PropTypes.string,
    roles: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  }),
};

const CanadianPreOrderEnrollmentPage = ({ user, onAgreementStatusChange }) => (
  <EnrollmentSteps
    key={`${user?.commonId || user?.commonDealerId}|${user?.userId}`}
    user={user}
    onAgreementStatusChange={onAgreementStatusChange}
  />
);

CanadianPreOrderEnrollmentPage.propTypes = EnrollmentSteps.propTypes;

export default CanadianPreOrderEnrollmentPage;
